/**
 * A condition node on the linkage canvas (Feature 79, revision 1, D26): one
 * way out of its gate, numbered in the order tried, with what it tests in
 * short. A link to a step is dragged from its right side; nothing links into
 * it, its gate's tie aside.
 *
 * Resting the pointer on it, or the keyboard, opens its own card: its
 * conditions in full and its steps (D27).
 */
import { Handle, Position, type Node, type NodeProps } from '../../ui/flow.js';
import { HoverCard } from '../../ui/HoverCard.js';
import { useNodeCard } from './hovered.js';
import type { NodeCard } from './node-card.js';
import type { ConditionFlowNode } from './to-flow.js';

export type ConditionNodeData = ConditionFlowNode['data'] & Record<string, unknown>;
export type ConditionNodeType = Node<ConditionNodeData, 'condition'>;

/** How wide a condition node is drawn. */
export const CONDITION_WIDTH = 176;
const HANDLE = {
  width: 9,
  height: 9,
  background: 'var(--surface-card)',
  border: '1.5px solid var(--text-soft)',
};
const TIE = { opacity: 0, width: 1, height: 1, minWidth: 0, minHeight: 0, border: 'none' };

/** Its kind as its head says it: `1 · if`, `2 · else if`, `else`. */
export function conditionHead(data: Pick<ConditionNodeData, 'kind' | 'number'>): string {
  return data.number === null ? data.kind : `${data.number} · ${data.kind}`;
}

/** The condition node's card: its title, then each line. */
function ConditionCardView({ card }: { card: NodeCard }) {
  return (
    <>
      <span className="mono" style={{ fontWeight: 'var(--weight-semibold)' }}>
        {card.title}
      </span>
      {(card.branches ?? []).map((line) => (
        <span
          key={line.text}
          className="mono"
          style={{
            fontSize: 'var(--text-meta)',
            color: line.faint ? 'var(--text-faint)' : 'var(--text-strong)',
          }}
        >
          {line.text}
        </span>
      ))}
    </>
  );
}

export function ConditionNode({ id, data, selected }: NodeProps<ConditionNodeType>) {
  const card = useNodeCard(id);
  const named = data.number !== null;
  return (
    <HoverCard open={card !== null} content={card && <ConditionCardView card={card} />}>
      <div
        style={{
          width: CONDITION_WIDTH,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: '5px 9px',
          borderRadius: 'var(--radius-control)',
          background: 'var(--blue-tint)',
          border: selected ? '1.5px solid var(--blue)' : '1px solid var(--border-input)',
        }}
      >
        <Handle type="target" position={Position.Left} isConnectable={false} style={TIE} />
        <span
          className="mono"
          style={{
            fontSize: 'var(--text-meta)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--blue)',
          }}
        >
          {conditionHead(data)}
        </span>
        {named && (
          <span
            className="mono"
            style={{ fontSize: 'var(--text-body-md)', fontWeight: 'var(--weight-semibold)' }}
          >
            {data.branch}
          </span>
        )}
        <span
          title={data.words}
          style={{
            fontSize: 'var(--text-meta)',
            color: data.empty ? 'var(--warn-ink)' : 'var(--text-soft)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {data.words}
        </span>
        <Handle type="source" position={Position.Right} style={HANDLE} />
      </div>
    </HoverCard>
  );
}
